import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction
} from 'mobx';

import type { RootStore } from 'widget/stores/RootStore';
import {
  WidgetContactFormFieldType,
  WidgetContactFormSubmitStatus
} from 'widget/entities/config';
import type { WidgetContactForm } from 'widget/entities/config';
import LoadingStageModel from 'widget/models/LoadingStageModel';
import {
  createTextLengthValidator,
  validateEmail,
  validateName,
  validatePhone,
  EMAIL_MAX_LENGTH,
  PHONE_MAX_LENGTH
} from 'widget/utils/validators';
import FormFieldModel from 'widget/models/FormFieldModel';
import ContactFieldModel from 'widget/models/ContactFieldModel';

import { WidgetContactsData } from '../ApiStore/types';

export class ContactFormStore {
  private _rootStore: RootStore;

  contactFormStatus: FormFieldModel<WidgetContactFormSubmitStatus> =
    new FormFieldModel<WidgetContactFormSubmitStatus>(
      WidgetContactFormSubmitStatus.pending
    );

  contacts: Record<WidgetContactFormFieldType, ContactFieldModel> =
    {} as Record<WidgetContactFormFieldType, ContactFieldModel>;

  readonly submitStage = new LoadingStageModel();

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;

    makeObservable(this, {
      contacts: observable.ref,

      contactFields: computed,
      isDone: computed,

      init: action,
      submitContacts: action
    });
  }

  get contactFields(): ContactFieldModel[] {
    return Object.values(this.contacts);
  }

  get isDone(): boolean {
    return (
      this.contactFields.length === 0 ||
      this.contactFormStatus.value === WidgetContactFormSubmitStatus.submitted
    );
  }

  init({
    fields,
    status
  }: {
    fields: WidgetContactForm['fields'];
    status: WidgetContactFormSubmitStatus;
  }): void {
    this.contacts = {} as Record<WidgetContactFormFieldType, ContactFieldModel>;

    for (const field of fields) {
      switch (field.type) {
        case WidgetContactFormFieldType.email:
          this.contacts[field.type] = new ContactFieldModel({
            type: field.type,
            required: field.required,
            validators: [
              createTextLengthValidator({ max: EMAIL_MAX_LENGTH }),
              validateEmail
            ]
          });
          break;

        case WidgetContactFormFieldType.phone:
          this.contacts[field.type] = new ContactFieldModel({
            type: field.type,
            required: field.required,
            validators: [
              createTextLengthValidator({ max: PHONE_MAX_LENGTH }),
              validatePhone
            ]
          });
          break;

        case WidgetContactFormFieldType.name:
          this.contacts[field.type] = new ContactFieldModel({
            type: field.type,
            required: field.required,
            validators: [validateName]
          });
          break;
      }
    }
    this.contactFormStatus.changeValue(status);
  }

  submitContacts = async (): Promise<{ isError: boolean }> => {
    if (this.submitStage.isLoading) {
      return { isError: true };
    }

    this.submitStage.loading();

    const data = {} as WidgetContactsData;
    for (const field of this.contactFields) {
      const trimmed = field.value.trim();
      if (!trimmed) {
        continue;
      }

      switch (field.type) {
        case WidgetContactFormFieldType.phone:
          data.phone = trimmed;
          break;

        case WidgetContactFormFieldType.email:
          data.email = trimmed;
          break;

        case WidgetContactFormFieldType.name:
          data.name = trimmed;
          break;
      }
    }

    try {
      const response = await this._rootStore.apiStore.submitContacts(data);

      runInAction(() => {
        if (response.isError) {
          this.submitStage.error();
          return;
        }
        this.contactFormStatus.changeValue(
          WidgetContactFormSubmitStatus.submitted
        );
        this.submitStage.success();
      });

      return { isError: response.isError };
    } catch {
      this.submitStage.error();

      return { isError: true };
    }
  };
}
